class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this)),
    );
  }

  /* ⚠ PIN THE CONFIRMATION UNDER THE FLOATING HEADER ONCE THE PAGE HAS MOVED.
     ==========================================================================
     Dawn hangs this panel `position: absolute` from the header, and in Dawn
     that works because the whole header SECTION is sticky and carries it
     along. This theme's header is a floating card: component-header.js pins
     only `.header__wrapper` (`position: fixed`) and the section stays in flow,
     so the panel stays at the TOP OF THE DOCUMENT while the customer is
     further down it.

     Measured 2026-09-29 at 1440 on mono-sherry25, scrolled to the buy button
     and adding: the panel's top was at -229px. The header came back, but
     "Item added to your cart", the product and View cart were all above the
     viewport; only CHECK OUT and Continue shopping hung below the card. On a
     1366x768 laptop the buy button is below the fold, so that is the ordinary
     case there, not an edge.

     Decided by measuring, not by breakpoint: if the panel's own anchor is
     still below the pinned card, the CSS position is right and is left alone
     (the top of the page; the phone layout, which already measured correct at
     126px both ways). Only when the anchor has scrolled up past the card is
     the panel pinned — under the card's RESTING bottom, `top` + `offsetHeight`
     from computed style, because `reveal()` may have just started the card's
     slide-in and its live rect is mid-transition. `left` is carried over from
     the absolute position so the panel does not jump sideways; `right` is
     released because the viewport is now its containing block, not the
     page-width box, and the two differ on a wide screen. */
  anchorToHeader() {
    const style = this.notification.style;
    style.removeProperty('position');
    style.removeProperty('top');
    style.removeProperty('left');
    style.removeProperty('right');

    const card = this.header?.querySelector('.header__wrapper');
    const anchor = this.notification.parentElement;
    if (!card || !anchor || getComputedStyle(card).position !== 'fixed') return;

    const cardBottom = (parseFloat(getComputedStyle(card).top) || 0) + card.offsetHeight;
    if (anchor.getBoundingClientRect().top >= cardBottom) return;

    const left = this.notification.getBoundingClientRect().left;
    style.position = 'fixed';
    style.top = `${cardBottom}px`;
    style.left = `${left}px`;
    style.right = 'auto';
  }

  open() {
    this.anchorToHeader();
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        }),
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    /* Optional-called, not assumed: the header component was rewritten once
       and lost this method, and the throw here swallowed the confirmation on
       every add (component-header.js, `reveal`). Revealing the header is a
       nicety; opening the confirmation is not. */
    this.header?.reveal?.();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
